/** @jsx h */
import { h } from 'preact'
import { createContext, useContext } from 'preact/compat'
import { useImmerReducer } from 'use-immer'

const GlobalStateContext = createContext()
const GlobalDispatchContext = createContext()

const closeAllSteps = steps => {
  Object.keys(steps).forEach(el => {
    steps[el].open = false
  })
}

const removeHighlightNextSteps = steps => {
  let removeHighlight = false
  Object.keys(steps).forEach((el, i) => {
    if (removeHighlight) {
      steps[el].highlighted = false
      return
    }

    if (!steps[el].done || steps[el].open) {
      removeHighlight = true
      return
    }

    steps[el].highlighted = true
  })
}

const resetStep = step => {
  step.open = false
  step.done = false
  step.enabled = false
}

const reducer = (draft, action) => {
  switch (action.type) {
    case 'set-toggled-boutique': {
      draft.toggledBoutiqueId = action.payload
      break
    }
    case 'set-country': {
      draft.selectedCountry = action.payload.code
      draft.preselectedBoutiqueId = null
      draft.toggledBoutiqueId = null
      break
    }
    case 'set-step-disabled': {
      draft.steps[action.payload].enabled = false
      break
    }
    case 'reset-toggled-boutique-id': {
      draft.toggledBoutiqueId = null
      break
    }
    case 'set-purpose-message': {
      draft.purposeMessage = action.payload
      break
    }
    case 'go-to-1': {
      draft.selectedBoutique = action.payload
      closeAllSteps(draft.steps)
      draft.steps[0].done = true
      draft.steps[0].highlighted = true
      resetStep(draft.steps[1])
      draft.steps[1].open = true
      draft.steps[1].enabled = true
      draft.steps[2].enabled = false
      draft.steps[3].enabled = false
      resetStep(draft.steps[2])

      // reset below
      draft.selectedPurpose = {}
      draft.purposeMessage = ''
      draft.appointmentDetails = {}
      break
    }
    case 'go-to-2': {
      closeAllSteps(draft.steps)
      draft.steps[1].done = true
      draft.steps[1].highlighted = true
      resetStep(draft.steps[2])
      draft.steps[2].open = true
      draft.steps[2].enabled = true
      draft.steps[3].enabled = false

      // reset below
      draft.appointmentDetails = {}
      break
    }
    case 'go-to-3': {
      draft.appointmentDetails = action.payload
      closeAllSteps(draft.steps)
      draft.steps[2].done = true
      draft.steps[2].highlighted = true
      draft.steps[3].open = true
      draft.steps[3].enabled = true
      break
    }
    case 'save-personal-info-data-only': {
      draft.personalInfo = action.payload
      break
    }
    case 'set-personal-info-data': {
      // last step
      closeAllSteps(draft.steps)
      draft.personalInfo = action.payload
      draft.steps[3].done = true
      draft.steps[3].highlighted = true
      break
    }
    case 'select-purpose': {
      draft.selectedPurpose = action.payload
      break
    }
    case 'set-serial-number': {
      draft.serialNumber = action.payload
      break
    }
    case 'set-ready': {
      draft.ready = true
      break
    }
    case 'toggle-step': {
      const isOpen = draft.steps[action.payload.index].open
      closeAllSteps(draft.steps)
      draft.steps[action.payload.index].open = !isOpen
      removeHighlightNextSteps(draft.steps)
      break
    }
    case 'slot-taken': {
      closeAllSteps(draft.steps)
      draft.steps[1].done = true
      draft.steps[1].enabled = true
      resetStep(draft.steps[2])
      draft.steps[2].open = true
      draft.steps[3].enabled = false
      draft.steps[3].done = false
      draft.hasSlotTakenError = true
      draft.appointmentDetails.AD_Available_Time = null
      break
    }
    case 'clear-slot-error': {
      draft.hasSlotTakenError = false
      break
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

function GlobalProvider({ children, maisonConfig, canvasConfig }) {
  const [state, dispatch] = useImmerReducer(reducer, {
    ready: false,
    hasSlotTakenError: false,
    maisonConfig,
    canvasConfig,
    preselectedBoutiqueId: canvasConfig.boutiqueId || null,
    toggledBoutiqueId: null,
    selectedCountryLabel: '',
    selectedCountry: canvasConfig.country ? canvasConfig.country.toUpperCase() : null,
    selectedBoutique: {},
    selectedPurpose: {},
    purposeMessage: '',
    serialNumber: null,
    appointmentDetails: {},
    personalInfo: {},
    steps: {
      0: {
        open: true,
        done: false,
        enabled: true,
        highlighted: false
      },
      1: {
        open: false,
        done: false,
        enabled: false,
        highlighted: false
      },
      2: {
        open: false,
        done: false,
        enabled: false,
        highlighted: false
      },
      3: {
        open: false,
        done: false,
        enabled: false,
        highlighted: false
      }
    }
  })

  return (
    <GlobalStateContext.Provider value={state}>
      <GlobalDispatchContext.Provider value={dispatch}>{children}</GlobalDispatchContext.Provider>
    </GlobalStateContext.Provider>
  )
}

function useGlobalState() {
  return useContext(GlobalStateContext)
}

function useGlobalDispatch() {
  return useContext(GlobalDispatchContext)
}

function useGlobal() {
  return [useGlobalState(), useGlobalDispatch()]
}

export { GlobalProvider, useGlobal }
